import { useAuth0 } from "@auth0/auth0-react";
import Cookies from "js-cookie";
import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";

import { Controller, useForm } from "react-hook-form";
import {
  GeistHookFormCompanyTypeSelect,
  GeistHookFormCountrySelect,
  GeistHookFormDatePicker,
  GeistHookFormEducationPicker,
  GeistHookFormFile,
  GeistHookFormInput,
  GeistHookFormLegitimationDocumentPicker,
  GeistHookFormProfessionPicker,
  GeistHookFormSalutationSelect,
  GeistHookFormTitleSelect,
} from "../components/geist-form-inputs";
import LoadingScreen from "../../../../components/loading-screen";
import { USER_STATE } from "../../../../types/generic_types";
import {
  EDUCATION,
  Investment,
  PROFESSION,
} from "../../../../types/investment_types";
import {
  DOCUMENT_TYPE,
  INVESTOR_TYPE,
  OnboardingData,
  OnboardingUpdateData,
  OnboardingZero,
} from "../../../../types/onboarding_types";
import API_CLIENT from "../../../../utility/api-client";
import { AppContext } from "../../direct-router";
import {
  InventureUser,
  LegalPerson,
  LEGAL_ENTITY_TYPE,
} from "../../../../types/inventure-user_types";
import { InvestmentContextFull } from "../investment-process-full";
import  {
  BenefitingPersonManagerV2,
} from "../../../../components/data-display/benefiting-persons";
import { useHistory } from "react-router-dom";
import { Text, Grid, Spacer, Card, Button, useTheme, Divider, Checkbox, Tooltip } from "@geist-ui/core";
import { Info, ArrowLeft } from "@geist-ui/icons";
import { MultistepFormButtonsV2 } from "../../../../components/vertical-multistep-form/multistep-form-buttons";


type PersonalDataProps = {
  back: () => void;
  next: () => void;
};

export function PersonalData({ back, next }: PersonalDataProps) {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [legal_person, setLegalPerson] = useState(false);
  const app_context = useContext(AppContext);
  const investment_context = useContext(InvestmentContextFull);
  const user_state = app_context.userState;
  const user_type = app_context.user?.investor_type;
  const user = app_context.user;
  const history = useHistory();
  const theme = useTheme();

  useEffect(() => {
    if (user_state) setLoading(false);
  }, [user_state]);

  if (loading) {
    return <LoadingScreen />;
  }

  const is_onboarded_1 = user_state == USER_STATE.ONBOARDED_1;
  const is_onboarded_2 = user_state == USER_STATE.ONBOARDED_2;
  const is_legal = user_type == INVESTOR_TYPE.LEGAL_PERSON;
  const has_legal_person = app_context.user?.legal_person != null;
  const has_benefiting_person = user?.benefiting_persons
    ? user.benefiting_persons.length > 0
    : false;

  const user_state_zero = user_state == USER_STATE.ZERO;
  const user_state_nat = user_state == USER_STATE.NONE;
  const user_state_legal =
    (is_onboarded_1 && is_legal && !has_legal_person) ||
    (is_onboarded_2 && is_legal && !has_legal_person);
  const user_state_benefiting =
    (is_onboarded_1 &&
      is_legal &&
      has_legal_person &&
      !has_benefiting_person) ||
    (is_onboarded_2 && is_legal && has_legal_person && !has_benefiting_person);

  const user_state_onboarded =
    user_state == USER_STATE.ONBOARDED_1 ||
    user_state == USER_STATE.ONBOARDED_2;
  const investment_id = investment_context.investment?.id;

  if (legal_person && investment_id) {
    const LegalInvestmentDialog = () => {
      return (
        <Card width="100%">
          <Text h3>Zeichnung als juristische Person</Text>
          <Text p>
            Sie können hier eine Investition über eine juristische Person starten.
          </Text>
          <Spacer h={2}/>
          <div className="flex flex-col gap-4">
            <Card className="bg-gray-50 border-0">
               <Text b>1. Beitrittserklärung anfordern</Text>
               <Text p small>Sie können die Beitrittserklärung für juristische Personen bequem über unsere Onlineplattform im nächsten Schritt anfordern.</Text>
            </Card>
             <Card className="bg-gray-50 border-0">
               <Text b>2. Beitrittserklärung online oder offline ausfüllen</Text>
               <Text p small>Im nächsten Schritt muss die Beitrittserklärung ausgefüllt werden (YouSign oder PDF).</Text>
            </Card>
             <Card className="bg-gray-50 border-0">
               <Text b>3. Hochladen der Beitrittserklärung</Text>
               <Text p small>(Falls offline ausgefüllt)</Text>
            </Card>
             <Card className="bg-gray-50 border-0">
               <Text b>4. Unterschreiben</Text>
               <Text p small>Über unseren Partner YouSign können Sie direkt unterschreiben.</Text>
            </Card>
          </div>
          <Spacer h={2} />
          
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <motion.button
              whileHover={{ x: -4 }}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-black bg-white text-black font-bold text-sm uppercase tracking-tight transition-all"
              onClick={() => setLegalPerson(false)}
            >
              <ArrowLeft size={18} />
              ZURÜCK
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 px-8 py-4 rounded-full bg-black text-white font-extrabold text-sm uppercase tracking-tight shadow-xl transition-all"
              onClick={() =>
                API_CLIENT.makeBindingOfferV3Legal(
                  app_context.token,
                  investment_id,
                  (investment: Investment) => {
                    investment_context.setInvestment(investment);
                    history.push("/direct/dashboard?investment_id=" + investment.id);
                  },
                  (error: string) => console.log(error),
                  ""
                )
              }
            >
              Investition starten
            </motion.button>
          </div>
        </Card>
      );
    };

    return <LegalInvestmentDialog />;
  }

  return (
    <div className="w-full max-w-4xl min-h-screen flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <Text h3 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
          Persönliche Informationen
        </Text>
        <Text p type="secondary" style={{ margin: 0, fontSize: 'clamp(14px, 4vw, 16px)', lineHeight: 1.5 }}>
          Bitte geben Sie Ihre persönlichen Daten an, um die Investition fortzusetzen.
        </Text>
      </div>

      {isAuthenticated ? (
        <div className="flex flex-col gap-8">
          {user_state_zero ? (
            <PersonalDataZero />
          ) : user_state_nat ? (
            <PersonalDataTwo />
          ) : user_state_legal ? (
            <PersonalDataLegalPerson />
          ) : user_state_benefiting ? (
            <PersonalDataBenefitingPerson />
          ) : user_state_onboarded ? (
             <PersonalDataTwoUpdate />
          ) : null}
          
           <Card width="100%" style={{ backgroundColor: theme.palette.accents_1, border: 'none' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                     <Text b style={{ fontSize: '0.9rem' }}>Juristische Person?</Text>
                     <Text small style={{ display: 'block', color: theme.palette.accents_5 }}>Möchten Sie über eine GmbH oder UG investieren?</Text>
                </div>
                 <a
                  href="https://meetings.hubspot.com/dat-le"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium hover:underline text-primary"
                >
                  Termin vereinbaren →
                </a>
            </div>
          </Card>
        </div>
      ) : (
        <Card width="100%">
           <div className="flex flex-col items-center text-center py-8">
               <Text h4>Anmeldung erforderlich</Text>
               <Text type="secondary" style={{ maxWidth: '400px', marginBottom: '2rem' }}>
                   Um mit der Investition fortzufahren, müssen Sie sich einloggen oder einen neuen Account erstellen.
               </Text>
               <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                   <motion.button 
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-8 py-4 rounded-full border border-black bg-white text-black font-bold text-sm uppercase tracking-tight"
                        onClick={() => {
                            loginWithRedirect({
                            appState: {
                                returnTo: `${window.location.pathname}${window.location.search}`,
                            },
                        });
                        }}
                    >
                        Einloggen
                    </motion.button>
                    <motion.button 
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-8 py-4 rounded-full bg-black text-white font-extrabold text-sm uppercase tracking-tight shadow-lg"
                        onClick={() => {
                            loginWithRedirect({
                                authorizationParams: {
                                    screen_hint: 'signup',
                                },
                                appState: {
                                    returnTo: `${window.location.pathname}${window.location.search}`,
                                },
                            });
                        }}
                    >
                        Account erstellen
                    </motion.button>
               </div>
           </div>
        </Card>
      )}
    </div>
  );
}

function PersonalDataZero() {
  const app_context = useContext(AppContext);
  const investment_context = useContext(InvestmentContextFull);
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<any>({ mode: "onChange" });

  function submit(data: OnboardingZero) {
    investment_context.setLoading(true);
    var affiliate_program = "";
    var cookie = Cookies.get("iv-affiliate");
    if (cookie != undefined) {
      affiliate_program = cookie;
    }
    data.affiliate_program = affiliate_program;
    API_CLIENT.putOnboardingZero(
      app_context.token,
      data,
      (user) => {
        app_context.setUserState(USER_STATE.NONE);
        app_context.updateUser(user);
      },
      () => {
        console.log("error");
      }
    );
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Text h4 style={{ fontFamily: '"Nunito Sans", sans-serif', marginTop: 0, marginBottom: '1.5rem' }}>Stammdaten</Text>
      
      <Grid.Container gap={2}>
        <Grid xs={24} md={12}>
          <div className="w-full">
            <GeistHookFormSalutationSelect
                control={control}
                name={"salutation"}
                error={false}
                label={"Anrede*"}
                required
            />
          </div>
        </Grid>
        <Grid xs={24} md={12}>
           <div className="w-full">
            <GeistHookFormTitleSelect
                control={control}
                name={"title"}
                error={false}
                label={"Titel"}
            />
           </div>
        </Grid>
        <Grid xs={24} md={12}>
           <div className="w-full">
            <GeistHookFormInput
                required
                control={control}
                name={"firstName"}
                error={errors.firstname != null}
                label={"Vorname*"}
            />
           </div>
        </Grid>
        <Grid xs={24} md={12}>
           <div className="w-full">
            <GeistHookFormInput
                required
                control={control}
                name={"lastName"}
                error={errors.surname != null}
                label={"Nachname*"}
            />
           </div>
        </Grid>

        <Grid xs={24}>
           <div className="w-full">
            <GeistHookFormInput
                control={control}
                defaultValue={""}
                name={"phoneNumber"}
                error={false}
                label={"Telefonnummer*"}
                required
            />
           </div>
        </Grid>
      </Grid.Container>

      <Spacer h={2} />
      
      <Spacer h={2} />
      
      <MultistepFormButtonsV2
        back={false}
        backFunction={() => null}
        disabled={!isValid}
      />
    </form>
  );
}

function PersonalDataTwo() {
  const investment_context = useContext(InvestmentContextFull);
  const app_context = useContext(AppContext);
  const theme = useTheme();
  
  const {
    control: control1,
    formState: { errors: errors1, isValid },
    handleSubmit: handleSubmit1,
  } = useForm<any>({ mode: "onChange" });

  interface PersonData {
    is_legal_person: boolean;
    investor_type?: INVESTOR_TYPE;
    occupation?: PROFESSION;
    educational_qualification?: EDUCATION;
    birthdate: string | Date;
    birthplace: string;
    citizenship: string;
    birthcountry: string;
    country: string;
    street: string;
    address_addition: string;
    zip: string;
    city: string;
    pep_status: boolean;
    is_beneficiary: boolean;
    document_id: string;
    document_valid_from: string | Date;
    document_valid_to: string | Date;
    document_type: DOCUMENT_TYPE;
    document_issuer: string;
  }

  const submitStep = (data: PersonData) => {
    var campaign = investment_context.campaign;
    var amount = investment_context.amount;

    if (campaign == null || amount == null) return;
    
    investment_context.setLoading(true);
    let o_data = {
      investor_type: data.is_legal_person
        ? INVESTOR_TYPE.NATURAL_PERSON // Default to natural person, legal person has own flow
        : INVESTOR_TYPE.NATURAL_PERSON,
      document_type: data.document_type,
      document_issuer: data.document_issuer,
      document_valid_from: data.document_valid_from.toString(),
      document_valid_to: data.document_valid_to.toString(),
      birthdate: data.birthdate.toString(),
      birthplace: data.birthplace,
      birthcountry: data.birthcountry,
      citizenship: data.citizenship,
      street: data.street,
      city: data.city,
      zip: data.zip,
      country: data.country,
      pep_status: data.pep_status,
      is_beneficiary: data.is_beneficiary,
      occupation: data.occupation,
      educational_qualification: data.educational_qualification,
      address_addition: data.address_addition,
      document_id: data.document_id,
    } as OnboardingData;

    API_CLIENT.putOnboarding(
      app_context.token,
      o_data,
      (user: InventureUser) => {
        API_CLIENT.putLegitimationDocumentToInvestment(
          app_context.token,
          investment_context.investment?.id + "",
          user.identification.document_id + "|" + user.identification.document_type,
          (investment: Investment) => {
            app_context.updateUser(user);
            investment_context.setInvestment(investment);
            app_context.setUserState(USER_STATE.ONBOARDED_1);
            (window as any).dataLayer.push({
              event: "onboarding-form-sent",
            });
          }
        );
      },
      (error) => console.log(error)
    );
  };

  // Common card style for consistency
  const sectionCardStyle = {
      borderRadius: '16px',
      border: '1px solid #EAEAEA',
      boxShadow: 'none',
      padding: 'clamp(16px, 3vw, 24px)',
      marginBottom: '1.5rem',
      backgroundColor: '#FFFFFF',
      width: '100%'
  };

  const sectionTitleStyle = {
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: 'clamp(16px, 4vw, 18px)',
      fontWeight: 700,
      marginTop: 0,
      marginBottom: '1.5rem'
  };

  return (
    <form onSubmit={handleSubmit1(submitStep)}>
      {/* HINTERGRUND SECTION */}
      <Card width="100%" style={sectionCardStyle}>
        <Text h4 style={sectionTitleStyle}>
          Hintergrund
        </Text>
        <Grid.Container gap={2}>
            <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormProfessionPicker
                        name="occupation"
                        defaultValue={PROFESSION.NO_ANSWER}
                        error={false}
                        control={control1}
                        label="Beruf*"
                        required
                    />
                </div>
            </Grid>
            <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormEducationPicker
                        name="educational_qualification"
                        defaultValue={EDUCATION.NO_ANSWER}
                        error={false}
                        control={control1}
                        label="Bildungsabschluss*"
                        required
                    />
                </div>
            </Grid>
             <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormCountrySelect
                        name="citizenship"
                        defaultValue={"DEU"}
                        error={false}
                        control={control1}
                        label="Staatsbürgerschaft*"
                        required
                    />
                </div>
            </Grid>
             <Grid xs={24} md={12} /> {/* Spacer */}
             
             <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormDatePicker
                        name="birthdate"
                        control={control1}
                        label="Geburtsdatum*"
                        defaultValue={null}
                        error={errors1.birthdate != null}
                        required
                    />
                </div>
             </Grid>
             <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="birthplace"
                        control={control1}
                        label="Geburtsort*"
                        required
                        defaultValue={null}
                        error={errors1.birthplace != null}
                    />
                </div>
             </Grid>
             <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormCountrySelect
                        name="birthcountry"
                        defaultValue={"DEU"}
                        error={false}
                        control={control1}
                        label="Geburtsland*"
                        required
                    />
                </div>
             </Grid>
        </Grid.Container>
      </Card>

      {/* ADRESSE SECTION */}
      <Card width="100%" style={sectionCardStyle}>
        <Text h4 style={sectionTitleStyle}>
          Adresse
        </Text>
        <Grid.Container gap={2}>
            <Grid xs={24}>
                <div className="w-full">
                    <GeistHookFormCountrySelect
                        name="country"
                        defaultValue={"DEU"}
                        error={false}
                        control={control1}
                        label="Land*"
                        required
                    />
                </div>
            </Grid>
            <Grid xs={24} md={16}>
                <div className="w-full">
                    <GeistHookFormInput
                        required
                        name="street"
                        control={control1}
                        label="Straße und Hausnummer*"
                        defaultValue={null}
                        error={errors1.street != null}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={8}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="address_addition"
                        control={control1}
                        label="Adresszusatz"
                        defaultValue={null}
                        error={false}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={8}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="zip"
                        control={control1}
                        label="Postleitzahl*"
                        required
                        defaultValue={null}
                        error={errors1.zip != null}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={16}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="city"
                        required
                        control={control1}
                        label="Ort*"
                        defaultValue={null}
                        error={errors1.city != null}
                    />
                </div>
            </Grid>
        </Grid.Container>
      </Card>

      {/* LEGITIMATIONSDOKUMENT SECTION */}
      <Card width="100%" style={sectionCardStyle}>
        <Text h4 style={sectionTitleStyle}>
          Legitimationsdokument
        </Text>
        
        <Card className="bg-orange-50 border-orange-200 text-orange-900 mb-6" style={{ backgroundColor: '#fff7ed', borderColor: '#fed7aa', color: '#7c2d12', padding: 'clamp(12px, 3vw, 20px)', borderRadius: '12px', boxShadow: 'none' }}>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                 <Info size={20} />
                 <Text p small className="m-0">Geben Sie hier die Daten zu Ihrem Reisepass oder Ausweis an, <b>mit dem Sie sich legitimieren möchten</b>.</Text>
            </div>
        </Card>

        <Grid.Container gap={2}>
            <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormLegitimationDocumentPicker
                        name="document_type"
                        defaultValue={DOCUMENT_TYPE.IDCARD.toString()}
                        error={false}
                        control={control1}
                        label="Dokumenttyp*"
                        required
                    />
                </div>
            </Grid>
            <Grid xs={24} md={12}>
                 <div className="w-full">
                    <GeistHookFormInput
                        name="document_id"
                        control={control1}
                        label="Ausweisnummer*"
                        defaultValue={null}
                        required
                        error={errors1.document_id != null}
                    />
                 </div>
            </Grid>
             <Grid xs={24}>
                 <div className="w-full">
                    <GeistHookFormInput
                        name="document_issuer"
                        control={control1}
                        label="Ausstellende Behörde*"
                        defaultValue={null}
                        required
                        error={errors1.document_issuer != null}
                    />
                 </div>
            </Grid>
            <Grid xs={24} md={12}>
                 <div className="w-full">
                    <GeistHookFormDatePicker
                        name="document_valid_from"
                        control={control1}
                        label="Ausgestellt am*"
                        defaultValue={null}
                        error={errors1.document_valid_from != null}
                        required
                    />
                 </div>
            </Grid>
            <Grid xs={24} md={12}>
                 <div className="w-full">
                    <GeistHookFormDatePicker
                        name="document_valid_to"
                        control={control1}
                        label="Gültig bis*"
                        defaultValue={null}
                        error={errors1.document_valid_to != null}
                        required
                    />
                 </div>
            </Grid>
        </Grid.Container>
      </Card>

      {/* CONFIRMATION SECTION */}
      <Card width="100%" style={sectionCardStyle}>
           <Controller
              name="pep_status"
              control={control1}
              rules={{ required: true }}
              defaultValue={false}
              render={({ field: { onChange, value } }) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                     <div style={{ transform: "scale(1.33)", transformOrigin: "top left", marginRight: "10px", marginTop: "2px" }}>
                       <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} />
                     </div>
                     <Text small style={{ flex: 1 }}>
                       Hiermit bestätige ich, dass ich selbst <b>keine politisch exponierte Person (PEP)</b>, kein Familienmitglied einer PEP und keine einer PEP bekanntermaßen nahestehende Person im Sinne der anwendbaren Geldwäschegesetzgebung bin.
                       <Tooltip text={'Eine politisch exponierte Person (PEP) ist eine Person, die selbst ein hochrangiges wichtiges öffentliches Amt ausübt...'}>
                            <Info size={16} color={theme.palette.accents_4} style={{ marginLeft: '8px', cursor: 'help', verticalAlign: 'middle' }} />
                       </Tooltip>
                     </Text>
                </div>
              )}
            />
            
            <Controller
              name="is_beneficiary"
              control={control1}
              defaultValue={false}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                 <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                     <div style={{ transform: "scale(1.33)", transformOrigin: "top left", marginRight: "10px", marginTop: "2px" }}>
                        <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} />
                     </div>
                     <Text small style={{ flex: 1 }}>Ich versichere, auf <b>eigene Rechnung und im eigenen wirtschaftlichen Interesse</b> zu handeln.</Text>
                 </div>
              )}
            />
            
            {(errors1.is_beneficiary || errors1.pep_status) && (
                <Text small type="error" className="mt-4 block">
                    Sie können nur investieren, sofern Sie keine politisch exponierte Person sind und auf eigene Rechnung handeln.
                </Text>
            )}
      </Card>

      <Spacer h={2} />
      
      <MultistepFormButtonsV2
        back={true}
        backFunction={() => investment_context.prevStep()}
        disabled={!isValid}
      />
    </form>
  );
}

function PersonalDataLegalPerson() {
  const investment_context = useContext(InvestmentContextFull);
  const app_context = useContext(AppContext);
  const theme = useTheme();
  
  const [identityFile, setIdentityFile] = useState<File | null>(null);
  const [structureFile, setStructureFile] = useState<File | null>(null);

  const {
    control,
    formState,
    handleSubmit,
  } = useForm<any>({ mode: "onChange" });

  const onSubmit = (data: any) => {
    if (!structureFile || !identityFile) return;

    API_CLIENT.postLegalPerson(
      app_context.token,
      (user) => {
        API_CLIENT.postLegalPersonFiles(
          app_context.token,
          () => {
            app_context.updateUser(user);
          },
          structureFile,
          identityFile
        );
      },
      data as LegalPerson
    );
  };
  
  const person = app_context.user?.legal_person;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8">
         <Text h4 style={{ fontFamily: '"Nunito Sans", sans-serif', borderBottom: `1px solid ${theme.palette.border}`, paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>
          Unternehmensdaten
        </Text>
        <Grid.Container gap={2}>
           <Grid xs={24} md={12}>
              <div className="w-full">
                <GeistHookFormInput
                    name="company_name"
                    control={control}
                    label="Unternehmensname*"
                    defaultValue={person?.company_name}
                    error={false}
                    required
                />
              </div>
           </Grid>
           <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormCompanyTypeSelect
                    name="legal_entity_type"
                    control={control}
                    label="Unternehmensform*"
                    defaultValue={person?.legal_entity_type}
                    error={false}
                    required
                />
               </div>
           </Grid>
           <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormInput
                    name="commercial_register_number"
                    control={control}
                    label="Handelsregisternummer*"
                    defaultValue={person?.commercial_register_number}
                    error={false}
                    required
                />
               </div>
           </Grid>
           <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormInput
                    name="commercial_register"
                    control={control}
                    label="Handelsregister*"
                    defaultValue={person?.commercial_register}
                    error={false}
                    required
                />
               </div>
           </Grid>
           <Grid xs={24}>
               <div className="w-full">
                <GeistHookFormInput
                    name="phone"
                    control={control}
                    label="Telefonnummer*"
                    required
                    defaultValue={person?.phone}
                    error={false}
                />
               </div>
           </Grid>
        </Grid.Container>
      </div>
      
      <div className="mb-8">
         <Text h4 style={{ fontFamily: '"Nunito Sans", sans-serif', borderBottom: `1px solid ${theme.palette.border}`, paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>
          Adresse
        </Text>
        <Grid.Container gap={2}>
             <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormInput
                    name="street"
                    control={control}
                    label="Straße und Hausnummer*"
                    defaultValue={person?.street}
                    error={false}
                    required
                />
               </div>
           </Grid>
           <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormInput
                    name="city"
                    control={control}
                    label="Stadt*"
                    defaultValue={person?.city}
                    error={false}
                    required
                />
               </div>
           </Grid>
           <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormInput
                    name="zip"
                    control={control}
                    label="Postleitzahl*"
                    defaultValue={person?.zip}
                    error={false}
                    required
                />
               </div>
           </Grid>
           <Grid xs={24} md={12}>
               <div className="w-full">
                <GeistHookFormCountrySelect
                    name="country"
                    control={control}
                    label="Land*"
                    defaultValue={person?.country}
                    error={false}
                    required
                />
               </div>
           </Grid>
        </Grid.Container>
      </div>
      
      <div className="mb-8">
         <Text h4 style={{ fontFamily: '"Nunito Sans", sans-serif', borderBottom: `1px solid ${theme.palette.border}`, paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>
          Gesellschaftsunterlagen
        </Text>
        <Text p type="secondary">Bitte laden Sie einen aktuellen Handelsregisterauszug (nicht älter als 2 Monate) sowie die Liste der Gesellschafter hoch.</Text>
        
        <Grid.Container gap={2}>
            <Grid xs={24} md={12}>
                <div className="w-full text-center">
                    <Text p small>Handelsregisterauszug</Text>
                    <GeistHookFormFile onFileDrop={(file) => setIdentityFile(file)} />
                </div>
            </Grid>
             <Grid xs={24} md={12}>
                <div className="w-full text-center">
                    <Text p small>Liste der Gesellschafter</Text>
                    <GeistHookFormFile onFileDrop={(file) => setStructureFile(file)} />
                </div>
            </Grid>
        </Grid.Container>
      </div>

      <Spacer h={2} />
      
      <MultistepFormButtonsV2
        back={false}
        backFunction={() => null}
        disabled={!formState.isValid || structureFile == null || identityFile == null}
      />
    </form>
  );
}

function PersonalDataBenefitingPerson() {
  const theme = useTheme();
  return (
    <div className="flex flex-col gap-8">
      <div>
        <Text small type="secondary" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Begünstigte Person</Text>
        <Text h3 style={{ fontFamily: '"Nunito Sans", sans-serif', marginTop: '0.5rem' }}>
          Bitte geben Sie hier die wirtschaftlich begünstigten Personen ein.
        </Text>
      </div>
      <BenefitingPersonManagerV2 />
    </div>
  );
}

function PersonalDataTwoUpdate() {
  const investment_context = useContext(InvestmentContextFull);
  const app_context = useContext(AppContext);
  const theme = useTheme();
  
  const {
    control: control1,
    formState: { errors: errors1, isValid },
    handleSubmit: handleSubmit1,
  } = useForm<any>({ mode: "onChange" });

  interface PersonData {
    occupation?: PROFESSION;
    educational_qualification?: EDUCATION;
    citizenship: string;
    country: string;
    street: string;
    address_addition: string;
    zip: string;
    city: string;
    pep_status: boolean;
    is_beneficiary: boolean;
    document_id: string;
    document_valid_from: string | Date;
    document_valid_to: string | Date;
    document_type: DOCUMENT_TYPE;
    document_issuer: string;
  }
  
  const submitStep = (data: PersonData) => {
    var campaign = investment_context.campaign;
    var amount = investment_context.amount;

    if (campaign == null || amount == null) return;
    
    investment_context.setLoading(true);
    let o_data = {
      document_type: data.document_type,
      document_issuer: data.document_issuer,
      document_valid_from: data.document_valid_from.toString(),
      document_valid_to: data.document_valid_to.toString(),
      citizenship: data.citizenship,
      street: data.street,
      city: data.city,
      zip: data.zip,
      country: data.country,
      pep_status: data.pep_status,
      is_beneficiary: data.is_beneficiary,
      occupation: data.occupation,
      educational_qualification: data.educational_qualification,
      address_addition: data.address_addition,
      document_id: data.document_id,
    } as OnboardingUpdateData;

    API_CLIENT.putOnboardingUpdate(
      app_context.token,
      o_data,
      (user: InventureUser) => {
        API_CLIENT.putLegitimationDocumentToInvestment(
          app_context.token,
          investment_context.investment?.id + "",
          user.identification.document_id + "|" + user.identification.document_type,
          (investment: Investment) => {
            app_context.updateUser(user);
            investment_context.setInvestment(investment);
            app_context.setUserState(USER_STATE.ONBOARDED_1);
            (window as any).dataLayer.push({
              event: "onboarding-form-sent",
            });
          }
        );
      },
      (error) => console.log(error)
    );
  };
  
  const user = app_context.user;

   // Common card style for consistency
  const sectionCardStyle = {
      borderRadius: '16px',
      border: '1px solid #EAEAEA',
      boxShadow: 'none',
      padding: 'clamp(16px, 3vw, 24px)',
      marginBottom: '1.5rem',
      backgroundColor: '#FFFFFF',
      width: '100%'
  };

  const sectionTitleStyle = {
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: 'clamp(16px, 4vw, 18px)',
      fontWeight: 700,
      marginTop: 0,
      marginBottom: '1.5rem'
  };

  return (
    <form onSubmit={handleSubmit1(submitStep)}>
       {/* REUSE LAYOUT FROM PersonalDataTwo but pre-filled */}
       <Card width="100%" style={sectionCardStyle}>
        <Text h4 style={sectionTitleStyle}>
          Hintergrund
        </Text>
        <Grid.Container gap={2}>
            <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormProfessionPicker
                        name="occupation"
                        defaultValue={user?.natural_person?.occupation}
                        error={false}
                        control={control1}
                        label="Beruf*"
                        required
                    />
                </div>
            </Grid>
            <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormEducationPicker
                        name="educational_qualification"
                        defaultValue={user?.natural_person?.educational_qualification}
                        error={false}
                        control={control1}
                        label="Bildungsabschluss*"
                        required
                    />
                </div>
            </Grid>
             <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormCountrySelect
                        name="citizenship"
                        defaultValue={"DEU"}
                        error={false}
                        control={control1}
                        label="Staatsbürgerschaft*"
                        required
                    />
                </div>
            </Grid>
        </Grid.Container>
      </Card>

       <Card width="100%" style={sectionCardStyle}>
        <Text h4 style={sectionTitleStyle}>
          Adresse
        </Text>
        <Grid.Container gap={2}>
            <Grid xs={24}>
                <div className="w-full">
                    <GeistHookFormCountrySelect
                        name="country"
                        defaultValue={user?.natural_person?.country}
                        error={false}
                        control={control1}
                        label="Land*"
                        required
                    />
                </div>
            </Grid>
            <Grid xs={24} md={16}>
                <div className="w-full">
                    <GeistHookFormInput
                        required
                        name="street"
                        control={control1}
                        label="Straße und Hausnummer*"
                        defaultValue={user?.natural_person?.street}
                        error={errors1.street != null}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={8}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="address_addition"
                        control={control1}
                        label="Adresszusatz"
                        defaultValue={user?.natural_person?.address_addition}
                        error={false}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={8}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="zip"
                        control={control1}
                        label="Postleitzahl*"
                        required
                        defaultValue={user?.natural_person?.zip}
                        error={errors1.zip != null}
                    />
                </div>
            </Grid>
            <Grid xs={24} md={16}>
                <div className="w-full">
                    <GeistHookFormInput
                        name="city"
                        required
                        control={control1}
                        label="Ort*"
                        defaultValue={user?.natural_person?.city}
                        error={errors1.city != null}
                    />
                </div>
            </Grid>
        </Grid.Container>
      </Card>

     {/* LEGITIMATIONSDOKUMENT SECTION */}
       <Card width="100%" style={sectionCardStyle}>
        <Text h4 style={sectionTitleStyle}>
          Legitimationsdokument
        </Text>
        
        <Card className="bg-orange-50 border-orange-200 text-orange-900 mb-6" style={{ backgroundColor: '#fff7ed', borderColor: '#fed7aa', color: '#7c2d12', padding: 'clamp(12px, 3vw, 20px)', borderRadius: '12px', boxShadow: 'none' }}>
            <div className="flex gap-4 items-center">
                 <Info size={20} />
                 <Text p small className="m-0">Geben Sie hier die Daten zu Ihrem Reisepass oder Ausweis an, <b>mit dem Sie sich legitimieren möchten</b>.</Text>
            </div>
        </Card>

        <Grid.Container gap={2}>
            <Grid xs={24} md={12}>
                <div className="w-full">
                    <GeistHookFormLegitimationDocumentPicker
                        name="document_type"
                        defaultValue={user?.identification?.document_type || DOCUMENT_TYPE.IDCARD.toString()}
                        error={false}
                        control={control1}
                        label="Dokumenttyp*"
                        required
                    />
                </div>
            </Grid>
            <Grid xs={24} md={12}>
                 <div className="w-full">
                    <GeistHookFormInput
                        name="document_id"
                        control={control1}
                        label="Ausweisnummer*"
                        defaultValue={user?.identification?.document_id}
                        required
                        error={errors1.document_id != null}
                    />
                 </div>
            </Grid>
             <Grid xs={24}>
                 <div className="w-full">
                    <GeistHookFormInput
                        name="document_issuer"
                        control={control1}
                        label="Ausstellende Behörde*"
                        defaultValue={user?.identification?.document_issuer}
                        required
                        error={errors1.document_issuer != null}
                    />
                 </div>
            </Grid>
            <Grid xs={24} md={12}>
                 <div className="w-full">
                    <GeistHookFormDatePicker
                        name="document_valid_from"
                        control={control1}
                        label="Ausgestellt am*"
                        defaultValue={user?.identification?.document_valid_from}
                        error={errors1.document_valid_from != null}
                        required
                    />
                 </div>
            </Grid>
            <Grid xs={24} md={12}>
                 <div className="w-full">
                    <GeistHookFormDatePicker
                        name="document_valid_to"
                        control={control1}
                        label="Gültig bis*"
                        defaultValue={user?.identification?.document_valid_to}
                        error={errors1.document_valid_to != null}
                        required
                    />
                 </div>
            </Grid>
        </Grid.Container>
      </Card>

       <Card width="100%" style={sectionCardStyle}>
           <Controller
              name="pep_status"
              control={control1}
              rules={{ required: true }}
              defaultValue={false}
              render={({ field: { onChange, value } }) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ transform: "scale(1.33)", transformOrigin: "top left", marginRight: "10px", marginTop: "2px" }}>
                       <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} />
                    </div>
                     <Text small style={{ flex: 1 }}>
                         Hiermit bestätige ich, dass ich selbst <b>keine politisch exponierte Person (PEP)</b>, kein Familienmitglied einer PEP und keine einer PEP bekanntermaßen nahestehende Person im Sinne der anwendbaren Geldwäschegesetzgebung bin.
                     </Text>
                </div>
              )}
            />
            
            <Controller
              name="is_beneficiary"
              control={control1}
              defaultValue={false}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                 <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div style={{ transform: "scale(1.33)", transformOrigin: "top left", marginRight: "10px", marginTop: "2px" }}>
                        <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} />
                    </div>
                    <Text small style={{ flex: 1 }}>Ich versichere, auf <b>eigene Rechnung und im eigenen wirtschaftlichen Interesse</b> zu handeln.</Text>
                 </div>
              )}
            />
      </Card>

      <Spacer h={2} />
      
      <MultistepFormButtonsV2
        back={true}
        backFunction={() => investment_context.prevStep()}
        disabled={Object.keys(errors1).length > 0}
      />
    </form>
  );
}
